/**
 * <quantity-bundle-price> — keeps the displayed price in sync with the two things
 * Liquid cannot render server-side: the quantity bundle radios (1x / 2x -5% /
 * 3x -10%) from the "quantity_bundle" block, and the selected selling plan from
 * the "subscription_picker" block. Both live in snippets/product-info.liquid.
 *
 * The element is rendered once per product-info and binds to both blocks by form
 * id rather than by nesting, because the two are independent: the Bürstenkopf and
 * Ersatzdüsen templates carry a subscription picker and no quantity bundle.
 *
 * The plan discount itself is never computed here. Prices come from
 * selling_plan_allocation.price via the JSON the picker emits, i.e. from Shopify's
 * own evaluation of the pricing policy, so the client cannot disagree with the
 * cart by a cent. Only the quantity tier is applied on top, floored per unit,
 * which is what Shopify's allocation_method: each does.
 *
 * How the theme does prices natively
 * ----------------------------------
 * Impact renders prices server-side and never recomputes them in the browser.
 * On a variant change the variant picker fetches the section for the new
 * combination, dispatches "product:rerender" on the product form, and
 * <product-rerender> swaps [data-block-type="price"] for the fresh markup.
 * "variant:change" follows, so by the time it fires the new markup is in place.
 *
 * The theme's own <quantity-selector> is NOT part of that: it only steps the
 * number input, and the price never multiplies. Volume pricing (the Plus/B2B
 * quantity_price_breaks) is a separate static table, not folded into the price.
 * There is nothing native to reuse here, because Liquid cannot render a price
 * for an arbitrary quantity — the Section Rendering API takes no quantity. The
 * multiplication therefore has to happen client-side, and this element keeps it
 * as close to price-list.liquid's own markup and classes as it can.
 *
 * Why a custom element instead of an inline script
 * -----------------------------------------------
 * The server markup always carries the per-unit price, so the multiplier has to
 * be re-applied after every swap. On a product change <product-rerender>
 * replaces its whole subtree, and injected <script> tags never execute — an
 * inline script would be dead from that point on. A custom element upgrades
 * itself whenever it lands in the document, which covers both cases.
 *
 * Depends on money.js (window.Zahnheld.formatMoney) and on the themeVariables
 * set inline by js-variables.liquid. Without the formatter the element leaves
 * the server-rendered price alone rather than printing a half-formatted number.
 */
(function () {
  'use strict';

  if (window.customElements.get('quantity-bundle-price')) return;

  function themeSettings() {
    return (window.themeVariables && window.themeVariables.settings) || {};
  }

  function themeStrings() {
    return (window.themeVariables && window.themeVariables.strings) || {};
  }

  function hasFormatter() {
    return !!(window.Zahnheld && window.Zahnheld.formatMoney);
  }

  /**
   * The discount-preview overlay rescales the very same unit-price string from the
   * same data-base-* attributes, using its own code-discount ratio. Composing the
   * two ratios correctly needs a joint pass through both scripts, so as long as the
   * preview is switched on the unit price is left to it rather than letting the two
   * overwrite each other with numbers that each ignore the other's discount. The
   * preview is off on main and dev, so today the bundle owns it.
   */
  function discountPreviewActive() {
    var preview = window.themeVariables && window.themeVariables.discountPreview;
    return !!(preview && preview.enabled);
  }

  /** Same format choice price-list.liquid makes between money and money_with_currency. */
  function moneyFormat() {
    var settings = themeSettings();
    if (settings.currencyCodeEnabled && settings.moneyWithCurrencyFormat) {
      return settings.moneyWithCurrencyFormat;
    }
    return settings.moneyFormat;
  }

  function formatMoney(cents) {
    return window.Zahnheld.formatMoney(cents, moneyFormat());
  }

  /**
   * The quantity tier applied to a per-unit price. The discount is floored per unit and
   * only then multiplied, which is what Shopify's allocation_method: each does. Verified
   * against real cart totals: 5 % of 2691 over 2 units is 5114, not 5113, and 5 % of 2990
   * over 2 units is 5682, not 5681.
   *
   * `unitBase` is already plan-adjusted when a subscription is selected, so the plan
   * discount is never recomputed here.
   */
  function tierTotal(unitBase, discountPercent, quantity) {
    var discountPerUnit = discountPercent > 0 ? Math.floor((unitBase * discountPercent) / 100) : 0;
    var perUnit = unitBase - discountPerUnit;
    return { perUnit: perUnit, price: perUnit * quantity };
  }

  /**
   * Replaces the visible content of a price element while keeping its .sr-only
   * label, which is what carries "Angebot" / "Regulärer Preis" for screen
   * readers. The money format comes from shop settings and may contain markup,
   * so it is parsed rather than set as text.
   */
  function setMoney(el, cents) {
    var srOnly = el.querySelector('.sr-only');
    while (el.firstChild) el.removeChild(el.firstChild);
    if (srOnly) el.appendChild(srOnly);
    el.appendChild(document.createRange().createContextualFragment(formatMoney(cents)));
  }

  /**
   * Writes text only when it actually changes. Used for the subscription widget,
   * which is watched by a MutationObserver: an unconditional assignment replaces
   * the text node even when the value is identical, and the resulting mutation
   * would call us straight back.
   */
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  /** Shows a pre-rendered unit price, or hides the element when there is none to show. */
  function setUnit(el, text) {
    if (!el) return;
    setText(el, text || '');
    el.hidden = !text;
  }

  /** Mirrors the savings string product-badges.liquid renders server-side. */
  function savingsLabel(compareCents, priceCents) {
    var value;
    if (themeSettings().discountMode === 'percentage' && compareCents > 0) {
      value = Math.round(((compareCents - priceCents) * 100) / compareCents) + '%';
    } else {
      value = formatMoney(compareCents - priceCents);
    }
    var template = themeStrings().discountBadge || '@@';
    if (template.indexOf('@@') === -1) return (template + ' ' + value).trim();
    return template.split('@@').join(value);
  }

  /**
   * Writes one price pair into a <price-list>. The class swap follows
   * price-list.liquid: the large PDP price only gains "text-on-sale", the
   * default-size one (sticky buy bar) trades "text-subdued" for it.
   */
  /** The unit price for `perUnitCents`, from the plan pair when there is one, else from the element's one-time pair. */
  function scaledUnitPrice(perUnitCents, dataset, planBase) {
    var usePlan = planBase && planBase.price > 0 && planBase.unitPrice > 0;
    var basePrice = usePlan ? planBase.price : Number(dataset.basePrice || 0);
    var baseUnitPrice = usePlan ? planBase.unitPrice : Number(dataset.baseUnitPrice || 0);
    if (!basePrice || !baseUnitPrice) return 0;
    return Math.round((baseUnitPrice * perUnitCents) / basePrice);
  }

  /**
   * The base price ("Grundpreis") has to follow the bundle discount, otherwise the
   * stated price per litre or per piece no longer matches the total beside it.
   *
   * It scales with the per-unit price, not with the quantity: buying three of a
   * 75 ml tube triples both the price and the volume, so the two cancel out and
   * only the discount moves the number.
   *
   * `planBase` ({ price, unitPrice } in cents) is the selected subscription allocation. Its unit price comes from
   * Shopify for exactly that price, so it replaces the one-time pair on the element: scaled from the one-time unit
   * price, which is itself rounded, 9,95 € for 75 ml came out as 132,66 €/l next to the 132,67 €/l of the plan card.
   */
  function applyToUnitPrice(list, perUnitCents, planBase) {
    if (discountPreviewActive()) return;

    var unitPrice = list.querySelector('unit-price');
    if (!unitPrice) return;

    var scaled = scaledUnitPrice(perUnitCents, unitPrice.dataset, planBase);
    if (!scaled) return;
    var strings = themeStrings();

    if (unitPrice.dataset.piece === '1') {
      var prefix = unitPrice.dataset.pieceAb === '1' ? strings.perPieceFromPrefix || '' : '';
      setText(unitPrice, '(' + prefix + formatMoney(scaled) + (strings.perPieceSuffix || '') + ')');
      return;
    }

    // Everything after the money part, as rendered by unit_price_with_measurement.
    // A reference that does not start with a slash means the split in price-list
    // did not work out, in which case the server value is the safer one to keep.
    var reference = unitPrice.dataset.unitReference || '';
    if (reference.charAt(0) !== '/') return;

    setText(unitPrice, '(' + formatMoney(scaled) + reference + ')');
  }

  function applyToPriceList(list, priceCents, compareAtCents) {
    var salePrice = list.querySelector('sale-price');
    if (!salePrice) return;

    var onSale = compareAtCents > priceCents;

    setMoney(salePrice, priceCents);
    salePrice.classList.toggle('text-on-sale', onSale);
    if (!list.classList.contains('price-list--lg')) {
      salePrice.classList.toggle('text-subdued', !onSale);
    }

    var compareAt = list.querySelector('compare-at-price');

    if (onSale) {
      if (!compareAt) {
        // The server only renders compare-at-price for a variant that is on
        // sale. A bundle discount creates the saving after the fact, so the
        // element has to be built here, in the same shape price-list.liquid
        // would have produced.
        compareAt = document.createElement('compare-at-price');
        compareAt.className = 'text-subdued line-through';
        compareAt.dataset.bundleInjected = '1';
        var label = themeStrings().regularPrice;
        if (label) {
          var srOnly = document.createElement('span');
          srOnly.className = 'sr-only';
          srOnly.textContent = label;
          compareAt.appendChild(srOnly);
        }
        salePrice.parentNode.insertBefore(compareAt, salePrice.nextSibling);
      }
      setMoney(compareAt, compareAtCents);
      compareAt.removeAttribute('hidden');
    } else if (compareAt) {
      if (compareAt.dataset.bundleInjected === '1') {
        compareAt.remove();
      } else {
        setMoney(compareAt, compareAtCents);
      }
    }
  }

  class QuantityBundlePrice extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      var signal = this._abortController.signal;

      this._basePrice = parseInt(this.getAttribute('base-price'), 10) || 0;
      this._baseCompareAt = parseInt(this.getAttribute('base-compare-at'), 10) || 0;

      // Delegated on document rather than bound to the inputs: <product-rerender>
      // replaces the quantity block wholesale on a variant change, and the drawer
      // injects a second set, so direct listeners would go stale.
      document.addEventListener('change', this._onControlChange.bind(this), { signal: signal });

      var form = this.form;
      if (form) {
        form.addEventListener('variant:change', this._onVariantChange.bind(this), { signal: signal });
      }

      this.update();
    }

    disconnectedCallback() {
      if (this._abortController) this._abortController.abort();
    }

    /**
     * Quantity radios, the purchase-type radios and the frequency radios all feed the
     * same price. Only the controls bound to this element's form are of interest.
     */
    _onControlChange(event) {
      var target = event.target;
      if (!target) return;

      var formId = this.getAttribute('form');

      if (target.matches && target.matches('[data-subscription-interval]')) {
        var owner = target.closest('[data-subscription-picker]');
        if (!owner || owner.dataset.formId !== formId) return;
        // Picking a frequency implies "I want the subscription", the way Seal behaves.
        this._syncIntervalToRadio(target);
        this.update();
        return;
      }

      if (target.name !== 'quantity' && target.name !== 'selling_plan') return;
      if (target.getAttribute('form') !== formId) return;
      this.update();
    }

    /** The subscription picker bound to this form, or null when the product has no plans. */
    get picker() {
      return document.querySelector(
        '[data-subscription-picker][data-form-id="' + this.getAttribute('form') + '"]'
      );
    }

    /** Per-variant prices and plan allocations, emitted as JSON by subscription-picker.liquid. */
    get planData() {
      if (this._planData !== undefined) return this._planData;
      var script = this.picker && this.picker.querySelector('[data-subscription-data]');
      if (!script) {
        this._planData = null;
        return null;
      }
      try {
        this._planData = JSON.parse(script.textContent);
      } catch (error) {
        this._planData = null;
      }
      return this._planData;
    }

    /** The picker's JSON entry for the variant currently in the form. */
    get variantData() {
      var data = this.planData;
      if (!data) return null;
      var form = this.form;
      var input = form && form.elements ? form.elements.namedItem('id') : null;
      var variantId = input && input.value ? String(input.value) : '';
      return data.variants[variantId] || data.variants[Object.keys(data.variants)[0]] || null;
    }

    /** Allocations for the variant currently in the form, keyed by selling plan id. */
    get allocations() {
      var variant = this.variantData;
      return (variant && variant.allocations) || {};
    }

    /** The chosen plan id, or '' for a one-time purchase. */
    get planId() {
      var checked = document.querySelector(
        'input[name="selling_plan"][form="' + this.getAttribute('form') + '"]:checked'
      );
      return checked && checked.value ? checked.value : '';
    }

    _syncIntervalToRadio(control) {
      var picker = this.picker;
      var aboInput = picker && picker.querySelector('[data-subscription-abo-input]');
      if (!aboInput) return;
      aboInput.value = control.value;
      if (!aboInput.checked) aboInput.checked = true;
    }

    /**
     * The product form. Note that form.id is shadowed by the hidden input named
     * "id" (the variant id), so the lookup goes through the document.
     */
    get form() {
      var id = this.getAttribute('form');
      if (!id) return null;
      return document.forms[id] || document.getElementById(id);
    }

    /** The section, not the closest <product-rerender>: the sticky bar has its own. */
    get scope() {
      return this.closest('.shopify-section') || document;
    }

    get priceLists() {
      var scope = this.scope;
      var formId = this.getAttribute('form');
      var lists = Array.prototype.slice.call(scope.querySelectorAll('[data-block-type="price"] price-list'));

      // The sticky buy bar sits in its own <product-rerender> outside the block
      // list, so it needs a selector of its own. Matching on the form id keeps
      // this to the bar of this product.
      if (formId) {
        scope.querySelectorAll('product-quick-add[form="' + formId + '"] price-list').forEach(function (list) {
          if (lists.indexOf(list) === -1) lists.push(list);
        });
      }

      return lists;
    }

    get selection() {
      var formId = this.getAttribute('form');
      var checked = document.querySelector(
        'input[type="radio"][name="quantity"][form="' + formId + '"]:checked'
      );
      if (checked) {
        return {
          quantity: parseInt(checked.value, 10) || 1,
          discount: parseFloat(checked.dataset.discount) || 0
        };
      }
      // Plain quantity selector (number input), or no quantity control at all.
      var field = document.querySelector('[name="quantity"][form="' + formId + '"]');
      return { quantity: (field && parseInt(field.value, 10)) || 1, discount: 0 };
    }

    /**
     * The totals in cents. `unitBase` is the plan-adjusted per-unit price when a
     * subscription is selected, so the tier percentage stacks on top of the plan
     * exactly as the cart does.
     */
    totalsFor(unitBase) {
      var selection = this.selection;
      var tier = tierTotal(unitBase, selection.discount, selection.quantity);
      var compareBase = this._baseCompareAt > this._basePrice ? this._baseCompareAt : this._basePrice;

      return {
        perUnit: tier.perUnit,
        price: tier.price,
        compareAt: compareBase * selection.quantity
      };
    }

    /** The totals for whatever is currently selected, subscription included. */
    get totals() {
      var planId = this.planId;
      var allocation = planId ? this.allocations[planId] : null;
      return this.totalsFor(allocation ? allocation.price : this._basePrice);
    }

    _onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      // The price block has already been swapped for this variant's per-unit
      // markup at this point, so all that is left is re-applying the multiplier.
      this._basePrice = variant.price;
      this._baseCompareAt = variant.compare_at_price || 0;
      this.update();
    }

    update() {
      if (!hasFormatter()) return;

      // Availability first: it can move the selection, which changes the headline price.
      this._updateSubscriptionRows();

      var totals = this.totals;
      var planId = this.planId;
      var allocation = planId ? this.allocations[planId] : null;
      var planBase = allocation ? { price: allocation.price, unitPrice: allocation.unitPriceCents } : null;

      this.priceLists.forEach(function (list) {
        applyToPriceList(list, totals.price, totals.compareAt);
        applyToUnitPrice(list, totals.perUnit, planBase);
      });

      this._updateBadge(totals.price, totals.compareAt);
      this._announce(totals.price);
    }

    /**
     * The frequency radios. Allocations are per variant, so a frequency can stop being
     * purchasable after a variant switch; its radio is disabled and hidden, and the
     * selection moves to the first purchasable one rather than submitting a plan Shopify
     * would reject. Returns the plan id that is selected afterwards, or '' without radios.
     */
    _settleInterval(picker, allocations) {
      var radios = Array.prototype.slice.call(picker.querySelectorAll('input[data-subscription-interval]'));
      if (!radios.length) return '';

      var firstAvailable = '';
      var current = '';

      radios.forEach(function (radio) {
        var available = !!allocations[radio.value];
        radio.disabled = !available;
        var option = radio.closest('.subscription-interval__option');
        if (option) option.hidden = !available;
        if (available && !firstAvailable) firstAvailable = radio.value;
        if (radio.checked && available) current = radio.value;
      });

      if (!current && firstAvailable) {
        current = firstAvailable;
        radios.forEach(function (radio) {
          radio.checked = radio.value === firstAvailable;
        });
      }

      return current;
    }

    /**
     * The two cards of the subscription picker, plus frequency availability. Copy and unit
     * prices are swapped in from the JSON the snippet rendered, never composed here.
     */
    _updateSubscriptionRows() {
      var picker = this.picker;
      if (!picker) return;

      var allocations = this.allocations;
      var aboInput = picker.querySelector('[data-subscription-abo-input]');

      var planId = this._settleInterval(picker, allocations) || (aboInput && aboInput.value);
      var allocation = planId ? allocations[planId] : null;

      if (aboInput) {
        if (allocation) {
          aboInput.value = planId;
          aboInput.disabled = false;
        } else {
          aboInput.disabled = true;
          if (aboInput.checked) {
            var oneTime = picker.querySelector('input[name="selling_plan"][value=""]');
            if (oneTime) oneTime.checked = true;
          }
        }
      }

      var oneTimeTotals = this.totalsFor(this._basePrice);
      var aboTotals = allocation ? this.totalsFor(allocation.price) : oneTimeTotals;

      var oneTimeEl = picker.querySelector('[data-subscription-onetime-price]');
      if (oneTimeEl) setText(oneTimeEl, formatMoney(oneTimeTotals.price));

      var aboEl = picker.querySelector('[data-subscription-abo-price]');
      if (aboEl) setText(aboEl, formatMoney(aboTotals.price));

      // Struck price on the subscription row is what the same quantity costs without a plan.
      var aboCompareEl = picker.querySelector('[data-subscription-abo-compare]');
      if (aboCompareEl) {
        setText(aboCompareEl, formatMoney(oneTimeTotals.price));
        if (oneTimeTotals.price > aboTotals.price) {
          aboCompareEl.removeAttribute('hidden');
        } else {
          aboCompareEl.setAttribute('hidden', '');
        }
      }

      var summaryEl = picker.querySelector('[data-subscription-summary]');
      if (summaryEl && allocation && allocation.summary) setText(summaryEl, allocation.summary);

      var fitEl = picker.querySelector('[data-subscription-fit]');
      if (fitEl) {
        var fit = (allocation && allocation.fit) || '';
        setText(fitEl, fit);
        fitEl.hidden = !fit;
      }

      // The per-piece price on each card is Shopify's, rendered for the plain quantity. A
      // quantity tier lowers it, and the bundle-scaled figure already sits in the main price
      // block, so the cards drop theirs rather than show a number the total contradicts.
      var tiered = this.selection.discount > 0;
      var variant = this.variantData;
      setUnit(picker.querySelector('[data-subscription-onetime-unit]'), tiered ? '' : variant && variant.rowUnitPrice);
      setUnit(picker.querySelector('[data-subscription-abo-unit]'), tiered ? '' : allocation && allocation.rowUnitPrice);
    }

    /**
     * One polite announcement per change for the whole picker. Putting aria-live on each
     * price node would fire several times for a single quantity change.
     */
    _announce(priceCents) {
      var picker = this.picker;
      var status = picker && picker.querySelector('[data-subscription-status]');
      if (!status) return;

      var checked = picker.querySelector('input[name="selling_plan"]:checked');
      var parts = [];

      if (checked) {
        var label = picker.querySelector('label[for="' + checked.id + '"] .subscription-option__name');
        if (label) parts.push(label.textContent.trim());
        if (checked.value) {
          var interval = picker.querySelector('input[data-subscription-interval]:checked');
          if (interval && interval.dataset.label) parts.push(interval.dataset.label);
        }
      }

      parts.push(this.selection.quantity + '×');
      parts.push(formatMoney(priceCents));
      setText(status, parts.join(', '));
    }

    /**
     * The saving badge next to the price has to state the cumulative saving
     * (variant sale plus bundle discount) or it contradicts the prices beside
     * it. It is only ever updated, never created: product-badges.liquid omits
     * both the badge and its container when the variant is not on sale, and the
     * bundle discount is already spelled out on the quantity chips themselves.
     */
    _updateBadge(priceCents, compareAtCents) {
      var badge = this.scope.querySelector('[data-block-type="price"] on-sale-badge');
      if (!badge) return;

      if (compareAtCents > priceCents) {
        while (badge.firstChild) badge.removeChild(badge.firstChild);
        badge.appendChild(document.createRange().createContextualFragment(savingsLabel(compareAtCents, priceCents)));
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    }

  }

  // Pure pricing helper exposed for tests/quantity-bundle-price.test.js, same convention
  // as window.__upsellPricing.
  window.__quantityBundlePricing = { tierTotal: tierTotal, scaledUnitPrice: scaledUnitPrice };

  window.customElements.define('quantity-bundle-price', QuantityBundlePrice);
})();
